import { lazy } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { AuthProvider, useAuth } from './context/AuthContext';
import { QueryProvider } from './context/QueryProvider';
import { CartProvider } from './context/CartContext';
import { SupportModalProvider } from './context/SupportModalContext';
import Navbar from '@design-components/Navbar'
import ScrollToTop from './components/ScrollToTop';
import ProtectedRoute from './features/auth/ProtectedRoute';
import LandingPage from '@client-pages/LandingPage';
import PublicProductsPage from '@client-pages/ProductsPage';
import ContactPage from '@client-pages/ContactPage';
import AboutPage from '@design-components/AboutPage';
import PublicLayout from '@design-layouts/PublicLayout';
import AdminLayout from './features/admin/layouts/AdminLayout';
import CustomerLayout from './features/customer/layouts/CustomerLayout';
import ForgotPasswordPage from './features/auth/ForgotPasswordPage';
import ResetPasswordPage from './features/auth/ResetPasswordPage';
import { Toaster } from 'sonner';
import WshDemoModal from '@wsh/WshDemoModal'
import LoadingSpinner from './components/LoadingSpinner';
import ThemeSync from './components/ThemeSync'
import DesignTokens from './components/DesignTokens'

import { IS_DEMO } from './constants/env'

// Admin pages - lazy loaded
const AdminDashboardPage = lazy(() => import('./features/admin/pages/OverviewPage'))
const AdminCustomersPage = lazy(() => import('./features/admin/pages/CustomersPage'))
const AdminOrdersPage = lazy(() => import('./features/admin/pages/OrdersPage'))
const AdminProductsPage = lazy(() => import('./features/admin/pages/ProductsPage'))
const AdminCategoriesPage = lazy(() => import('./features/admin/pages/CategoriesPage'))

const AdminManufacturersPage = lazy(() => import('./features/admin/pages/ManufacturersPage'))
const AdminFeaturedProductsPage = lazy(() => import('./features/admin/pages/FeaturedProductsPage'))
const AdminLiteraturePage = lazy(() => import('./features/admin/pages/LiteraturePage'))
const AdminNotificationsPage = lazy(() => import('./features/admin/pages/NotificationsPage'))
const AdminAccountPage = lazy(() => import('./features/admin/pages/AccountPage'))
const AdminBrandingPage = lazy(() => import('./features/admin/pages/BrandingPage'))
const StorefrontLayout = lazy(() => import('./features/admin/layouts/StorefrontLayout'))
const LandingEditPage = lazy(() => import('./features/admin/pages/LandingEditPage'))
const AboutEditPage = lazy(() => import('./features/admin/pages/AboutEditPage'))
const ContactEditPage = lazy(() => import('./features/admin/pages/ContactEditPage'))
const ProductsEditPage = lazy(() => import('./features/admin/pages/ProductsEditPage'))
const CatalogLayout = lazy(() => import('./features/admin/layouts/CatalogLayout'))
const SettingsLayout = lazy(() => import('./features/admin/layouts/SettingsLayout'))
const DevSupportPage = lazy(() => import('./features/admin/pages/DevSupportPage'))

// Customer portal pages - lazy loaded
const CustomerDashboardPage = lazy(() => import('./features/customer/pages/OverviewPage'))
const CustomerOrdersPage = lazy(() => import('./features/customer/pages/OrdersPage'))
const CustomerAccountPage = lazy(() => import('./features/customer/pages/AccountPage'))
const CustomerNotificationsPage = lazy(() => import('./features/customer/pages/NotificationsPage'))
const CustomerSettingsLayout = lazy(() => import('./features/customer/layouts/CustomerSettingsLayout'))

const AppWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

// Renders the demo gate modal only when the user is not yet authenticated.
// Sits outside the route tree so it overlays the products page background.
function DemoGate() {
  const { isAuthenticated, loading } = useAuth()
  if (loading || isAuthenticated) return null
  return <WshDemoModal />
}

function AppContent() {
  const { loading, isAuthenticated } = useAuth()

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <AppWrapper>
      <Router>
        <ScrollToTop />
        {(!IS_DEMO || isAuthenticated) && <Navbar />}
        <MainContent>
          {IS_DEMO ? (
            // ── Demo route tree ── products page as background + two portals only
            <Routes>
              <Route path="/products" element={<PublicProductsPage />} />

              <Route path="/customer-portal" element={
                <ProtectedRoute requiredRole="customer">
                  <CustomerLayout />
                </ProtectedRoute>
              }>
                <Route index element={<CustomerDashboardPage />} />
                <Route path="orders" element={<CustomerOrdersPage />} />
                <Route path="account" element={<Navigate to="/customer-portal/settings/account" replace />} />
                <Route path="notifications" element={<CustomerNotificationsPage />} />
                <Route path="messages" element={<Navigate to="/customer-portal/notifications" replace />} />
                <Route path="settings" element={<CustomerSettingsLayout />}>
                  <Route index element={<Navigate to="/customer-portal/settings/account" replace />} />
                  <Route path="account" element={<CustomerAccountPage />} />
                </Route>
              </Route>

              <Route path="/admin-portal" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminLayout />
                </ProtectedRoute>
              }>
                <Route index element={<AdminDashboardPage />} />
                <Route path="customers" element={<AdminCustomersPage />} />
                <Route path="orders" element={<AdminOrdersPage />} />
                <Route path="orders/messages" element={<Navigate to="/admin-portal/notifications" replace />} />
                <Route path="notifications" element={<AdminNotificationsPage />} />
                <Route path="storefront" element={<StorefrontLayout />}>
                  <Route index element={<Navigate to="/admin-portal/storefront/branding" replace />} />
                  <Route path="branding" element={<AdminBrandingPage />} />
                  <Route path="landing" element={<LandingEditPage />} />
                  <Route path="about" element={<AboutEditPage />} />
                  <Route path="contact" element={<ContactEditPage />} />
                  <Route path="products" element={<ProductsEditPage />} />
                </Route>
                <Route path="catalog" element={<CatalogLayout />}>
                  <Route index element={<Navigate to="/admin-portal/catalog/products" replace />} />
                  <Route path="products" element={<AdminProductsPage />} />
                  <Route path="categories" element={<AdminCategoriesPage />} />
                  <Route path="product-types" element={<Navigate to="/admin-portal/catalog/categories" replace />} />
                  <Route path="manufacturers" element={<AdminManufacturersPage />} />
                  <Route path="featured-products" element={<AdminFeaturedProductsPage />} />
                  <Route path="literature" element={<AdminLiteraturePage />} />
                </Route>
                <Route path="products" element={<Navigate to="/admin-portal/catalog/products" replace />} />
                <Route path="featured-products" element={<Navigate to="/admin-portal/catalog/featured-products" replace />} />
                <Route path="literature" element={<Navigate to="/admin-portal/catalog/literature" replace />} />
                <Route path="settings" element={<SettingsLayout />}>
                  <Route index element={<Navigate to="/admin-portal/settings/account" replace />} />
                  <Route path="storefront" element={<Navigate to="/admin-portal/storefront" replace />} />
                  <Route path="branding" element={<Navigate to="/admin-portal/storefront/branding" replace />} />
                  <Route path="account" element={<AdminAccountPage />} />
                  <Route path="dev-support" element={<DevSupportPage />} />
                </Route>
              </Route>

              {/* Everything else → products page (shows demo gate modal) */}
              <Route path="*" element={<Navigate to="/products" replace />} />
            </Routes>
          ) : (
            // ── Standard route tree ──
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<LandingPage />} />
                <Route path="/products" element={<PublicProductsPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
              </Route>

              <Route path="/customer-portal" element={
                <ProtectedRoute requiredRole="customer">
                  <CustomerLayout />
                </ProtectedRoute>
              }>
                <Route index element={<CustomerDashboardPage />} />
                <Route path="orders" element={<CustomerOrdersPage />} />
                <Route path="account" element={<Navigate to="/customer-portal/settings/account" replace />} />
                <Route path="notifications" element={<CustomerNotificationsPage />} />
                <Route path="messages" element={<Navigate to="/customer-portal/notifications" replace />} />
                <Route path="settings" element={<CustomerSettingsLayout />}>
                  <Route index element={<Navigate to="/customer-portal/settings/account" replace />} />
                  <Route path="account" element={<CustomerAccountPage />} />
                </Route>
              </Route>

              <Route path="/admin-portal" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminLayout />
                </ProtectedRoute>
              }>
                <Route index element={<AdminDashboardPage />} />
                <Route path="customers" element={<AdminCustomersPage />} />
                <Route path="orders" element={<AdminOrdersPage />} />
                <Route path="orders/messages" element={<Navigate to="/admin-portal/notifications" replace />} />
                <Route path="notifications" element={<AdminNotificationsPage />} />
                <Route path="storefront" element={<StorefrontLayout />}>
                  <Route index element={<Navigate to="/admin-portal/storefront/branding" replace />} />
                  <Route path="branding" element={<AdminBrandingPage />} />
                  <Route path="landing" element={<LandingEditPage />} />
                  <Route path="about" element={<AboutEditPage />} />
                  <Route path="contact" element={<ContactEditPage />} />
                  <Route path="products" element={<ProductsEditPage />} />
                </Route>
                <Route path="catalog" element={<CatalogLayout />}>
                  <Route index element={<Navigate to="/admin-portal/catalog/products" replace />} />
                  <Route path="products" element={<AdminProductsPage />} />
                  <Route path="categories" element={<AdminCategoriesPage />} />
                  <Route path="product-types" element={<Navigate to="/admin-portal/catalog/categories" replace />} />
                  <Route path="manufacturers" element={<AdminManufacturersPage />} />
                  <Route path="featured-products" element={<AdminFeaturedProductsPage />} />
                  <Route path="literature" element={<AdminLiteraturePage />} />
                </Route>
                <Route path="products" element={<Navigate to="/admin-portal/catalog/products" replace />} />
                <Route path="featured-products" element={<Navigate to="/admin-portal/catalog/featured-products" replace />} />
                <Route path="literature" element={<Navigate to="/admin-portal/catalog/literature" replace />} />
                <Route path="settings" element={<SettingsLayout />}>
                  <Route index element={<Navigate to="/admin-portal/settings/account" replace />} />
                  <Route path="storefront" element={<Navigate to="/admin-portal/storefront" replace />} />
                  <Route path="branding" element={<Navigate to="/admin-portal/storefront/branding" replace />} />
                  <Route path="account" element={<AdminAccountPage />} />
                  <Route path="dev-support" element={<DevSupportPage />} />
                </Route>
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          )}
        </MainContent>

        {/* Demo gate — rendered over the products page until authenticated */}
        {IS_DEMO && <DemoGate />}
      </Router>
      <Toaster richColors position="top-right" duration={3000} offset="75px" mobileOffset="70px" />
    </AppWrapper>
  );
}

function App() {
  return (
    <QueryProvider>
      <ThemeSync />
      <DesignTokens />
      <AuthProvider>
        <SupportModalProvider>
          <CartProvider>
            <AppContent />
          </CartProvider>
        </SupportModalProvider>
      </AuthProvider>
    </QueryProvider>
  )
}

export default App
